import { animate, state, style, transition, trigger } from '@angular/animations';
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';

import { Group, GroupService } from 'app/providers/semantria-api/group.service';
import { AssistantGuardService } from 'app/services/assistant-guard.service';
import { NavbarService } from 'app/services/navbar.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { Utils } from 'app/utils/utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-group-dropdown-wrapper',
  templateUrl: './group-dropdown-wrapper.component.html',
  styleUrls: ['./group-dropdown-wrapper.component.scss'],
  animations: [
    trigger('arrowState', [
      state(
        'closed',
        style({
          transform: 'rotate(0deg)',
        })
      ),
      state(
        'open',
        style({
          transform: 'rotate(180deg)',
        })
      ),
      transition('closed <=> open', animate(300)),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GroupDropdownWrapperComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() groups: Group[];
  @Input() selectedGroup: Group;
  @Input() groupSelectorTooltip: string;
  @Input() groupTab: boolean;

  public dropdownOpen = false;
  public state = 'closed';
  public selectedGroupName = '-No group-';

  constructor(
    private groupService: GroupService,
    private assistantGuardService: AssistantGuardService,
    private navbarService: NavbarService,
    private router: Router,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.groups?.currentValue || changes.selectedGroup?.currentValue) {
      this.setSelectedGroupName();
    }
  }

  onToggleDropdown(isClosed: boolean) {
    if (!this.groupTab) {
      isClosed ? (this.dropdownOpen = false) : (this.dropdownOpen = true);
      this.state === 'closed' ? (this.state = 'open') : (this.state = 'closed');
      this.cdr.markForCheck();
    }
  }

  public async setGroup(group: Group): Promise<void> {
    if (this.selectedGroup === group) {
      return;
    }
    const confirmed = await this.assistantGuardService.confirmGroupSwitch(group.group_name);
    if (!confirmed) {
      return;
    }
    // currentUrlSegments[0] should always be "" as location.path() always starts with a leading '/'
    const currentUrlSegments = this.location.path().split('/');
    const canDeactivate = await this.router.navigateByUrl('/dummy', { skipLocationChange: true });
    if (!canDeactivate) {
      return;
    }
    this.groupService.setSelectedGroup(group);
    this.selectedGroup = this.groupService.getSelectedGroup();
    this.setSelectedGroupName();
    this.navbarService.rebuildNavbar();
    if (currentUrlSegments[1] === 'groups') {
      currentUrlSegments[2] = GroupService.shortenGroupId(group.group_id);
      if (currentUrlSegments.length > 4) {
        currentUrlSegments.splice(4, currentUrlSegments.length - 4);
      }
    }
    if (currentUrlSegments[1] === 'd') {
      currentUrlSegments[1] = 'groups';
      currentUrlSegments[2] = GroupService.shortenGroupId(group.group_id);
      currentUrlSegments[3] = 'projects';
    }
    this.router.navigateByUrl(currentUrlSegments.join('/'));
  }

  setSelectedGroupName() {
    let groupName = '-No group-';
    if (this.selectedGroup) {
      groupName = this.selectedGroup.group_name;
    } else if (this.groups?.length) {
      groupName = this.groups[0].group_name;
    }

    this.selectedGroupName = Utils.truncateEnd(groupName, 25);

    this.cdr.markForCheck();
  }
}
